import type { ImageLoaderProps } from "next/image"

// Cloudflare Image Resizing loader for images already served from a Cloudflare-
// proxied host (cdn.alif.clothing — Transformations are enabled on that zone).
// We use the SAME-ZONE PATH form of /cdn-cgi/image/ so Cloudflare treats the
// source as a first-party image (no remote fetch): the resized variant is
// edge-cached + Polished and served straight from the CDN, which keeps image
// resizing off this Next origin's CPU (unlike /_next/image).
//
// format=auto → Cloudflare negotiates AVIF/WebP from the browser Accept header.
// fit=scale-down → never upscales past the original; the card CSS handles cropping.

// Cloudflare bills/limits by UNIQUE TRANSFORMATION = (source image × options).
// next/image asks the loader for every entry of deviceSizes+imageSizes, so an
// un-snapped loader mints up to 17 uniques per image — and because product
// originals are only ~945px wide, `fit=scale-down` returns BYTE-IDENTICAL output
// for every width at or above that (1080/1200/1920/2048/3840 = five uniques, one
// image). ~550 product images × that spread exhausted the free 5,000/month
// allowance; the zone then answered every not-yet-cached width with
// `HTTP 429 / cf-resized: err=9422` and next/image painted a broken <img>.
//
// Snapping each requested width UP to the nearest rung caps the spread at 7
// (realistically 2–3 per image) with no visual loss — a request only ever
// resolves to a width >= the one next/image asked for. Keep this ladder short.
const WIDTH_BUCKETS = [128, 256, 384, 640, 1024, 1536, 2048] as const

// Cap below deviceSizes' 3840: no source image in this catalogue approaches it,
// so a 3840 variant costs a unique transformation and returns the same pixels.
const MAX_WIDTH = WIDTH_BUCKETS[WIDTH_BUCKETS.length - 1]

export function snapWidth(width: number): number {
  return WIDTH_BUCKETS.find((bucket) => bucket >= width) ?? MAX_WIDTH
}

export default function cloudflareImageLoader({
  src,
  width,
  quality,
}: ImageLoaderProps): string {
  try {
    const u = new URL(src)
    const options = [
      `width=${snapWidth(width)}`,
      `quality=${quality || 80}`,
      "format=auto",
      "fit=scale-down",
    ].join(",")
    return `${u.origin}/cdn-cgi/image/${options}${u.pathname}${u.search}`
  } catch {
    // Relative/unparseable src — let next/image handle it unchanged.
    return src
  }
}
